/**
 * ColorShop — modal preset picker for the username-colour override.
 *
 * The legacy FFR site sold colour packs via the tokens economy; this is
 * the in-chat picker UX over the same `colorOverride` localStorage pref.
 * Buying tokens is out of scope; we expose a curated palette of 36 hexes
 * commonly seen on FFR (admin red, mod orange, dev purple, plus a 6×5
 * grid of pleasant brights).
 */
import { Modal } from './Modal.js';
import { usePrefs } from '../hooks/usePrefs.js';

const PRESETS: Array<{ name: string; hex: string }> = [
    { name: 'Default (rank)',    hex: '' },
    // Rank-traditional
    { name: 'Admin Red',         hex: '#FF0000' },
    { name: 'Mod Orange',        hex: '#FF6618' },
    { name: 'Forum Pink',        hex: '#E818FF' },
    { name: 'Dev Purple',        hex: '#8f0cc7' },
    { name: 'Event Blue',        hex: '#0062ca' },
    { name: 'Judge Green',       hex: '#13CC23' },
    { name: 'Music Yellow',      hex: '#F7E138' },
    { name: 'Elite Light Green', hex: '#63d644' },
    { name: 'Retired Grey',      hex: '#B4BBC4' },
    // Brights
    { name: 'Cyan',              hex: '#00d4ff' },
    { name: 'Teal',              hex: '#00b894' },
    { name: 'Mint',              hex: '#55efc4' },
    { name: 'Coral',             hex: '#ff7675' },
    { name: 'Salmon',            hex: '#fab1a0' },
    { name: 'Peach',             hex: '#ffaf60' },
    { name: 'Magenta',           hex: '#ff66cc' },
    { name: 'Hot Pink',          hex: '#ff3477' },
    { name: 'Lilac',             hex: '#a29bfe' },
    { name: 'Indigo',            hex: '#6c5ce7' },
    { name: 'Royal Blue',        hex: '#1a5dc0' },
    { name: 'Sky',               hex: '#74b9ff' },
    { name: 'Lime',              hex: '#a3e635' },
    { name: 'Olive',             hex: '#94834a' },
    { name: 'Gold',              hex: '#ffd070' },
    { name: 'Amber',             hex: '#ffb800' },
    { name: 'Rust',              hex: '#d35400' },
    { name: 'Maroon',            hex: '#6b0f0f' },
    { name: 'Slate',             hex: '#637495' },
    { name: 'Charcoal',          hex: '#2c3e50' },
    { name: 'Ice',               hex: '#dfe6f5' },
    { name: 'Cream',             hex: '#fff8d8' },
    { name: 'Plum',              hex: '#5b2867' },
    { name: 'Brown',             hex: '#5a3d2b' },
    { name: 'Navy',              hex: '#1a2540' },
    { name: 'White',             hex: '#ffffff' },
];

interface Props {
    onClose: () => void;
    onApply: (hex: string | null) => void;
}

export function ColorShop({ onClose, onApply }: Props) {
    const [prefs] = usePrefs();
    const current = prefs.colorOverride ?? '';

    return (
        <Modal title="Username Colour" onClose={onClose} className="pc-modal-colorshop">
            <p className="cs-blurb">
                Pick a colour for your username, or pick "Default (rank)" to use
                your rank colour. Saved locally.
            </p>
            <div className="cs-grid">
                {PRESETS.map((p, i) => {
                    const isCurrent = (p.hex === '' && !current) || p.hex.toLowerCase() === current.toLowerCase();
                    return (
                        <button
                            key={i}
                            className={`cs-swatch ${isCurrent ? 'cs-swatch-current' : ''}`}
                            title={p.hex ? `${p.name} — ${p.hex}` : p.name}
                            onClick={() => { onApply(p.hex || null); onClose(); }}>
                            <span className="cs-chip"
                                  style={{
                                      background: p.hex || 'transparent',
                                      border: p.hex ? '1px solid #2c2c2c' : '1px dashed #cdd9ec',
                                  }} />
                            <span className="cs-label" style={{ color: p.hex || '#ffffff' }}>
                                {p.name}
                            </span>
                        </button>
                    );
                })}
            </div>
        </Modal>
    );
}
